import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Archive,
  ArchiveRestore,
  Lock,
  MoreHorizontal,
  Pencil,
  Plus,
} from "lucide-react";
import {
  rolesLookupApi,
  type CreateRolePayload,
  type RoleDto,
  type UpdateRolePayload,
} from "@/api/lookups";
import { ApiError } from "@/api/client";
import {
  Badge,
  Button,
  FormField,
  Input,
} from "@/design-system/components/ui";
import { Modal } from "@/components/ui/Modal";
import { Textarea } from "@/components/ui/Select";
import { useToast } from "@/components/ui/Toast";
import { DataTable, type Column } from "@/components/layout/DataTable";
import { PageHeader } from "@/components/layout/PageHeader";
import { useAuth } from "@/hooks/useAuth";
import { cn } from "@/design-system/lib/utils";
import { useDateFormatter } from "@/utils/datetime";

/**
 * Admin-managed Lookups v1.0 §4a — admin CRUD for the Role taxonomy
 * table seeded by the AddAdminManagedLookups migration. List view with
 * search + show-archived toggle, kebab menu per row (Edit / Archive |
 * Unarchive), and a single create+edit modal that respects the
 * code-immutable-on-edit rule from spec §2d.
 *
 * The ADMIN row's Archive action is disabled with an explanatory
 * tooltip — system-reserved rows cannot be archived per spec §5
 * (returns 400 from the backend if attempted).
 *
 * Mutations invalidate both <c>['admin-roles']</c> (this page) and
 * <c>['roles']</c> (the user-facing lookup that drives RoleChip,
 * dropdowns, and the Report Template editor) so display-name changes
 * propagate immediately across the app.
 */
export function AdminRolesPage() {
  const { hasPermission } = useAuth();
  const canManage = hasPermission("Lookups.ManageRoles");
  const qc = useQueryClient();
  const { toast } = useToast();
  const { date } = useDateFormatter();
  const [search, setSearch] = useState("");
  const [showArchived, setShowArchived] = useState(false);
  const [editTarget, setEditTarget] = useState<RoleDto | "new" | null>(null);

  const list = useQuery({
    queryKey: ["admin-roles", true],
    // Always fetch with archived included so the toggle is purely a
    // client-side filter — toggling shouldn't trigger a refetch.
    queryFn: () => rolesLookupApi.adminList(true),
  });

  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ["admin-roles"] });
    void qc.invalidateQueries({ queryKey: ["roles"] });
  };

  const archiveMutation = useMutation({
    mutationFn: (role: RoleDto) =>
      role.isActive
        ? rolesLookupApi.adminArchive(role.code)
        : rolesLookupApi.adminUnarchive(role.code),
    onSuccess: (_data, role) => {
      toast({
        tone: "success",
        message: role.isActive
          ? `Archived ${role.code} · ${role.displayName}. Toggle 'Show archived' to find it again.`
          : `Restored ${role.code} · ${role.displayName}.`,
      });
      invalidate();
    },
    onError: (e: Error) => toast({ tone: "error", message: e.message }),
  });

  const filtered = (list.data ?? [])
    .filter((r) => (showArchived ? true : r.isActive))
    .filter((r) => {
      if (!search) return true;
      const needle = search.toLowerCase();
      return (
        r.code.toLowerCase().includes(needle) ||
        r.displayName.toLowerCase().includes(needle)
      );
    });
  const archivedCount = (list.data ?? []).filter((r) => !r.isActive).length;

  const columns: Column<RoleDto>[] = [
    {
      key: "code",
      header: "Code",
      render: (r) => (
        <span
          className={cn(
            "font-mono text-onyx font-semibold",
            !r.isActive && "opacity-50",
          )}
        >
          {r.code}
        </span>
      ),
    },
    {
      key: "displayName",
      header: "Display name",
      render: (r) => (
        <div className={cn(!r.isActive && "opacity-50")}>
          <div className="font-semibold text-onyx flex items-center gap-space-2">
            <span>{r.displayName}</span>
            {!r.isActive && <Badge status="archived">Archived</Badge>}
            {r.isSystemReserved && (
              <Badge
                status="info"
                title="System-reserved — cannot be archived."
              >
                <Lock size={10} aria-hidden className="inline mr-[2px]" />
                Reserved
              </Badge>
            )}
          </div>
          {r.description && (
            <div className="text-caption text-gray-mid font-normal max-w-md truncate">
              {r.description}
            </div>
          )}
        </div>
      ),
    },
    {
      key: "displayOrder",
      header: "Order",
      render: (r) => (
        <span className={cn("font-mono text-caption", !r.isActive && "opacity-50")}>
          {r.displayOrder}
        </span>
      ),
    },
    {
      key: "modified",
      header: "Last modified",
      render: (r) => (
        <span className={cn("text-caption text-gray-mid", !r.isActive && "opacity-50")}>
          {date(r.modifiedAt)}
        </span>
      ),
    },
    {
      key: "actions",
      header: "",
      render: (r) =>
        canManage ? (
          <RowActions
            role={r}
            archivePending={archiveMutation.isPending}
            onEdit={() => setEditTarget(r)}
            onArchiveToggle={() => archiveMutation.mutate(r)}
          />
        ) : null,
    },
  ];

  return (
    <>
      <PageHeader
        eyebrow="Admin"
        title="Roles"
        description="The platform's role taxonomy — codes, display names, and ordering. Archived roles disappear from new-assignment dropdowns but existing references survive. Codes are permanent once issued; rename the display name instead."
        actions={
          canManage && (
            <Button onClick={() => setEditTarget("new")}>
              <Plus size={16} aria-hidden /> New role
            </Button>
          )
        }
      />

      <div className="flex items-center gap-space-3 mb-space-3 flex-wrap">
        <Input
          type="search"
          placeholder="Search code or display name…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="max-w-sm"
        />
        <button
          type="button"
          onClick={() => setShowArchived((v) => !v)}
          aria-pressed={showArchived}
          className={cn(
            "px-space-2 py-[6px] rounded-full text-caption font-display font-semibold border transition-colors",
            showArchived
              ? "bg-gold/15 border-gold text-gold-shadow"
              : "bg-white-pure border-border-light text-gray-mid hover:border-gold-shadow",
          )}
        >
          Show archived{archivedCount > 0 ? ` (${archivedCount})` : ""}
        </button>
      </div>

      <DataTable
        columns={columns}
        rows={filtered}
        loading={list.isLoading}
        error={list.isError ? (list.error as Error).message : null}
        emptyMessage={
          canManage
            ? "No roles match your filter. Click 'New role' to add one."
            : "No roles available."
        }
        rowKey={(r) => r.code}
      />

      {editTarget !== null && (
        <RoleEditModal
          target={editTarget}
          existingCodes={list.data?.map((r) => r.code) ?? []}
          onClose={() => setEditTarget(null)}
          onSaved={() => {
            setEditTarget(null);
            invalidate();
          }}
        />
      )}
    </>
  );
}

function RowActions({
  role,
  archivePending,
  onEdit,
  onArchiveToggle,
}: {
  role: RoleDto;
  archivePending: boolean;
  onEdit: () => void;
  onArchiveToggle: () => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const archiveDisabled = role.isSystemReserved;
  const archiveTooltip = archiveDisabled
    ? `${role.code} is system-reserved and cannot be archived — it's required for system access.`
    : undefined;

  return (
    <div className="flex items-center gap-space-1 justify-end" ref={ref}>
      <button
        type="button"
        onClick={onEdit}
        className="p-space-1 rounded-md text-gold-shadow hover:bg-white-warm"
        aria-label={`Edit ${role.code}`}
      >
        <Pencil size={16} aria-hidden />
      </button>
      <div className="relative">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="p-space-1 rounded-md text-gray-mid hover:bg-white-warm hover:text-onyx"
          aria-label={`More actions for ${role.code}`}
          aria-haspopup="menu"
          aria-expanded={open}
        >
          <MoreHorizontal size={16} aria-hidden />
        </button>
        {open && (
          <div
            role="menu"
            className="absolute right-0 mt-1 w-64 z-20 bg-white-pure border border-border-light rounded-md shadow-lg overflow-hidden"
          >
            <button
              type="button"
              role="menuitem"
              disabled={archivePending || archiveDisabled}
              title={archiveTooltip}
              onClick={() => {
                setOpen(false);
                onArchiveToggle();
              }}
              className="w-full flex items-center gap-space-2 px-space-3 py-space-2 text-small text-onyx hover:bg-white-warm disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-transparent"
            >
              {role.isActive ? (
                <>
                  <Archive size={14} aria-hidden /> Archive
                </>
              ) : (
                <>
                  <ArchiveRestore size={14} aria-hidden /> Unarchive
                </>
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

interface RoleEditModalProps {
  target: RoleDto | "new";
  existingCodes: string[];
  onClose: () => void;
  onSaved: () => void;
}

const ROLE_CODE_REGEX = /^[A-Za-z0-9-]{1,20}$/;

function RoleEditModal({ target, existingCodes, onClose, onSaved }: RoleEditModalProps) {
  const isNew = target === "new";
  const initial: RoleDto | undefined = isNew ? undefined : target;
  const { toast } = useToast();
  const [code, setCode] = useState(initial?.code ?? "");
  const [displayName, setDisplayName] = useState(initial?.displayName ?? "");
  const [description, setDescription] = useState(initial?.description ?? "");
  const [displayOrder, setDisplayOrder] = useState<string>(
    initial?.displayOrder !== undefined ? String(initial.displayOrder) : "0",
  );
  const [errors, setErrors] = useState<string[]>([]);

  const createMutation = useMutation({
    mutationFn: (body: CreateRolePayload) => rolesLookupApi.adminCreate(body),
    onSuccess: (dto) => {
      toast({ tone: "success", message: `Created ${dto.code} · ${dto.displayName}.` });
      onSaved();
    },
    onError: (e: Error) => surfaceError(e),
  });
  const updateMutation = useMutation({
    mutationFn: ({ code, body }: { code: string; body: UpdateRolePayload }) =>
      rolesLookupApi.adminUpdate(code, body),
    onSuccess: (dto) => {
      toast({ tone: "success", message: `Saved ${dto.code} · ${dto.displayName}.` });
      onSaved();
    },
    onError: (e: Error) => surfaceError(e),
  });

  const surfaceError = (e: Error) => {
    if (e instanceof ApiError && e.errors.length) {
      setErrors(e.errors);
    } else {
      setErrors([e.message]);
    }
  };

  const validate = (): string[] => {
    const issues: string[] = [];
    if (isNew) {
      const trimmed = code.trim();
      if (!trimmed) issues.push("Code is required.");
      else if (!ROLE_CODE_REGEX.test(trimmed))
        issues.push("Code must be 1–20 letters, digits, or hyphens.");
      else if (
        existingCodes.some((c) => c.toLowerCase() === trimmed.toLowerCase())
      )
        issues.push(`Code '${trimmed}' already exists.`);
    }
    if (!displayName.trim()) issues.push("Display name is required.");
    if (displayName.length > 100)
      issues.push("Display name must be 100 characters or fewer.");
    if (description.length > 500)
      issues.push("Description must be 500 characters or fewer.");
    return issues;
  };

  const onSubmit = () => {
    setErrors([]);
    const issues = validate();
    if (issues.length) {
      setErrors(issues);
      return;
    }
    const order = parseInt(displayOrder, 10);
    const body = {
      displayName: displayName.trim(),
      description: description.trim() || null,
      displayOrder: Number.isFinite(order) ? order : 0,
    };
    if (isNew) {
      createMutation.mutate({ ...body, code: code.trim() });
    } else if (initial) {
      updateMutation.mutate({ code: initial.code, body });
    }
  };

  const pending = createMutation.isPending || updateMutation.isPending;

  return (
    <Modal
      open
      onClose={onClose}
      dismissOnBackdropClick={false}
      title={isNew ? "New role" : `Edit ${initial?.code}`}
      description={
        isNew
          ? "Add a new role to the taxonomy. The code is permanent once saved — pick something stable like 'B6'."
          : "Update the display name, description, or sort order. Codes are permanent because existing data references them."
      }
      size="md"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={pending}>
            Cancel
          </Button>
          <Button onClick={onSubmit} loading={pending}>
            {isNew ? "Create role" : "Save changes"}
          </Button>
        </>
      }
    >
      <div className="space-y-space-3">
        <FormField
          label="Code"
          required={isNew}
          htmlFor="role-code"
          helperText={
            isNew
              ? "Letters, digits, and hyphens only. Max 20 characters. Examples: B6, E1-VM, ADMIN."
              : "Codes are permanent — existing data references this code. Edit the display name instead if you need to rename the role."
          }
        >
          {isNew ? (
            <Input
              id="role-code"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder="e.g. B6"
              maxLength={20}
              autoFocus
              className="font-mono"
            />
          ) : (
            <div
              id="role-code"
              className="font-mono text-onyx font-semibold px-space-2 py-[6px] bg-white-warm border border-border-light rounded-md inline-block"
              title="Codes are permanent — existing data references this code."
            >
              {initial?.code}
            </div>
          )}
        </FormField>

        <FormField label="Display name" required htmlFor="role-name">
          <Input
            id="role-name"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            placeholder="e.g. Quality Operations Lead"
            maxLength={100}
            autoFocus={!isNew}
          />
        </FormField>

        <FormField
          label="Description"
          htmlFor="role-desc"
          helperText="Optional internal note. Not shown to non-admin users."
        >
          <Textarea
            id="role-desc"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={3}
            maxLength={500}
          />
        </FormField>

        <FormField
          label="Display order"
          htmlFor="role-order"
          helperText="Lower numbers appear first. Increment by 10 to leave room for future inserts."
        >
          <Input
            id="role-order"
            type="number"
            value={displayOrder}
            onChange={(e) => setDisplayOrder(e.target.value)}
            className="max-w-[8rem] font-mono"
          />
        </FormField>

        {errors.length > 0 && (
          <ul
            className="list-disc pl-space-4 text-caption text-status-error"
            role="alert"
          >
            {errors.map((e, i) => (
              <li key={i}>{e}</li>
            ))}
          </ul>
        )}
      </div>
    </Modal>
  );
}
